import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchBlogs, getImageUrl } from '../services/api';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiTag, FiSearch } from 'react-icons/fi';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 10;

  // Load initial page from URL params
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, []);

  // Fetch blogs
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        const blogsData = await fetchBlogs(pageSize, currentPage);
        setBlogs(blogsData.data);
        setTotalPages(blogsData.pagination.pageCount || 1);
      } catch (err) {
        console.error('Error loading blogs:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams({ page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900 text-white py-20">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">Insights & Stories</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto">
              Discover industry trends, expert tips, and breakthrough innovations
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading amazing content...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-600 text-red-700 px-6 py-4 rounded-lg flex items-start gap-4">
                <span className="text-2xl">⚠️</span>
                <div>
                  <p className="font-semibold">Error loading posts</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            ) : blogs.length === 0 ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-600 text-yellow-700 px-6 py-4 rounded-lg text-center">
                <p className="font-semibold text-lg">No posts yet</p>
                <p className="text-sm mt-1">Check back soon for exciting content!</p>
              </div>
            ) : (
              <>
                {/* Blog Cards */}
                <div className="space-y-6">
                  {blogs.map((blog, index) => (
                    <article
                      key={blog.id}
                      className="group bg-white rounded-2xl shadow-md hover:shadow-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="flex flex-col md:flex-row">
                        {/* Cover Image */}
                        {blog.cover && (
                          <div className="relative md:w-56 md:h-56 flex-shrink-0 overflow-hidden bg-gray-200">
                            <img
                              src={getImageUrl(blog.cover, 'medium')}
                              alt={blog.title}
                              className="w-full h-48 md:h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        )}

                        {/* Content */}
                        <div className={`p-6 md:p-8 flex-1 flex flex-col justify-between ${!blog.cover ? 'w-full' : ''}`}>
                          {/* Meta */}
                          <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <FiCalendar size={16} className="text-blue-600" />
                              <time>{formatDate(blog.createdAt)}</time>
                            </div>
                            {blog.category && (
                              <div className="flex items-center gap-1 px-3 py-1 bg-blue-50 rounded-full">
                                <FiTag size={16} className="text-blue-600" />
                                <span className="text-xs font-semibold text-blue-600">{blog.category.name}</span>
                              </div>
                            )}
                          </div>

                          {/* Title */}
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition">
                            <Link to={`/blog/${blog.slug}`}>
                              {blog.title}
                            </Link>
                          </h2>

                          {/* Description */}
                          <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                            {truncateText(blog.description, 200)}
                          </p>

                          {/* Read More Link */}
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group/link"
                          >
                            <span>Read Full Article</span>
                            <FiChevronRight className="ml-2 group-hover/link:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-12 pt-8 border-t border-gray-200">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2.5 rounded-lg border-2 border-gray-300 hover:border-blue-600 hover:bg-blue-50 text-gray-700 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      title="Previous page"
                    >
                      <FiChevronLeft size={20} />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2.5 rounded-lg font-semibold transition-all ${
                              currentPage === page
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'border-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2.5 rounded-lg border-2 border-gray-300 hover:border-blue-600 hover:bg-blue-50 text-gray-700 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      title="Next page"
                    >
                      <FiChevronRight size={20} />
                    </button>
                  </div>
                )}
                
                {/* Pagination info */}
                {totalPages > 1 && (
                  <div className="text-center mt-6 text-gray-600 text-sm">
                    Page <span className="font-semibold text-blue-600">{currentPage}</span> of <span className="font-semibold text-blue-600">{totalPages}</span>
                  </div>
                )}
              </>
            )}
      </div>
    </div>
  );
};

export default Blog;